<script setup lang="ts">
import type { Placement } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiDropdownMenu - Design System Dropdown Menu Component
 *
 * A dropdown menu for actions and navigation. The menu is teleported to the
 * document body and positioned with `fixed` coordinates derived from the
 * trigger's bounding rect, so it escapes ancestor `overflow: hidden`
 * (e.g. the rounded card container) instead of being clipped to it.
 *
 * The default slot receives `{ isOpen }` so a trigger can carry its own
 * `aria-expanded` — the component can't set it, since the trigger is whatever
 * the consumer passes in.
 *
 * @example
 * <UiDropdownMenu :items="menuItems">
 *   <UiButton>Options</UiButton>
 * </UiDropdownMenu>
 *
 * @example
 * <UiDropdownMenu :items="menuItems" v-slot="{ isOpen }">
 *   <button :aria-expanded="isOpen" aria-haspopup="menu">…</button>
 * </UiDropdownMenu>
 */
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

export interface DropdownMenuItem {
   /** Unique identifier */
   id: string;
   /** Display label */
   label: string;
   /** Icon to display */
   icon?: string;
   /** Whether the item is disabled */
   disabled?: boolean;
   /** Whether to show a divider after this item */
   divider?: boolean;
   /** Danger/destructive action styling */
   danger?: boolean;
}

export interface UiDropdownMenuProps {
   /** Menu items */
   items: DropdownMenuItem[];
   /** Placement relative to trigger */
   placement?: Extract<Placement, 'bottom' | 'bottom-start' | 'bottom-end' | 'top' | 'top-start' | 'top-end'>;
   /** Minimum width of the dropdown */
   minWidth?: string;
}

const props = withDefaults(defineProps<UiDropdownMenuProps>(), {
   placement: 'bottom-end',
   minWidth: '160px',
});

const emit = defineEmits<{
   select: [item: DropdownMenuItem];
}>();

const isOpen = ref(false);
const triggerRef = ref<HTMLDivElement | null>(null);
const menuRef = ref<HTMLDivElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

function toggleMenu() {
   isOpen.value = !isOpen.value;
}

function closeMenu() {
   isOpen.value = false;
}

function selectItem(item: DropdownMenuItem) {
   if (!item.disabled) {
      emit('select', item);
      closeMenu();
   }
}

// `position: fixed` coordinates derived from the trigger's viewport rect.
// For *-end and top-* placements we anchor via `right` / `bottom` so we don't
// need to measure the menu after render — the menu naturally extends inward.
function computePosition() {
   const trigger = triggerRef.value;
   if (!trigger)
      return;
   const r = trigger.getBoundingClientRect();
   const gap = 4;
   const style: Record<string, string> = { position: 'fixed' };
   switch (props.placement) {
      case 'bottom-end':
         style.top = `${r.bottom + gap}px`;
         style.right = `${window.innerWidth - r.right}px`;
         break;
      case 'bottom-start':
         style.top = `${r.bottom + gap}px`;
         style.left = `${r.left}px`;
         break;
      case 'bottom':
         style.top = `${r.bottom + gap}px`;
         style.left = `${r.left + r.width / 2}px`;
         style.transform = 'translateX(-50%)';
         break;
      case 'top-end':
         style.bottom = `${window.innerHeight - r.top + gap}px`;
         style.right = `${window.innerWidth - r.right}px`;
         break;
      case 'top-start':
         style.bottom = `${window.innerHeight - r.top + gap}px`;
         style.left = `${r.left}px`;
         break;
      case 'top':
         style.bottom = `${window.innerHeight - r.top + gap}px`;
         style.left = `${r.left + r.width / 2}px`;
         style.transform = 'translateX(-50%)';
         break;
   }
   menuStyle.value = style;
}

watch(isOpen, async (open) => {
   if (!open)
      return;
   await nextTick();
   computePosition();
});

function handleClickOutside(event: MouseEvent) {
   const target = event.target as Node;
   if (triggerRef.value?.contains(target))
      return;
   if (menuRef.value?.contains(target))
      return;
   closeMenu();
}

function handleEscape(event: KeyboardEvent) {
   if (event.key === 'Escape' && isOpen.value)
      closeMenu();
}

// Scroll inside any ancestor (sidebar, page main, nested overflow) and resize
// would otherwise leave the menu floating where the trigger used to be.
function handleViewportChange() {
   if (isOpen.value)
      computePosition();
}

onMounted(() => {
   document.addEventListener('click', handleClickOutside);
   document.addEventListener('keydown', handleEscape);
   window.addEventListener('resize', handleViewportChange);
   window.addEventListener('scroll', handleViewportChange, true);
});

onUnmounted(() => {
   document.removeEventListener('click', handleClickOutside);
   document.removeEventListener('keydown', handleEscape);
   window.removeEventListener('resize', handleViewportChange);
   window.removeEventListener('scroll', handleViewportChange, true);
});
</script>

<template>
   <div ref="triggerRef" class="inline-flex">
      <div @click="toggleMenu">
         <slot :is-open="isOpen" />
      </div>
   </div>

   <Teleport to="body">
      <Transition
         enter-active-class="transition duration-100 ease-out"
         enter-from-class="opacity-0 scale-95"
         enter-to-class="opacity-100 scale-100"
         leave-active-class="transition duration-75 ease-in"
         leave-from-class="opacity-100 scale-100"
         leave-to-class="opacity-0 scale-95"
      >
         <div
            v-if="isOpen"
            ref="menuRef"
            class="z-50 py-1 bg-white border border-neutral rounded-lg shadow-lg"
            :style="{ ...menuStyle, minWidth }"
            role="menu"
         >
            <template v-for="item in items" :key="item.id">
               <button
                  type="button"
                  role="menuitem"
                  class="w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors"
                  :class="[
                     item.disabled
                        ? 'opacity-50 cursor-not-allowed'
                        : item.danger
                           ? 'text-red-600 hover:bg-red-50'
                           : 'text-black hover:bg-neutral-light',
                  ]"
                  :disabled="item.disabled"
                  @click="selectItem(item)"
               >
                  <Icon v-if="item.icon" :icon="item.icon" class="size-4 flex-shrink-0" />
                  <span>{{ item.label }}</span>
               </button>
               <div v-if="item.divider" class="my-1 border-t border-neutral" />
            </template>
         </div>
      </Transition>
   </Teleport>
</template>
